import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  getQuestionListBySubTopic,
  loadMoreQuestionListBySubTopic,
  getSessionById,
} from './SessionApi';
import { IQuestion } from '../QuestionSlice';

export interface ISessionQuestion {
  pageNo: number;
  question: IQuestion;
  answer?: {
    _id: string;
    isCorrect: boolean;
    content: string;
  };
}

export interface ISession {
  questions: Array<ISessionQuestion>;
  subTopicId: string;
  student: string;
  _id?: string;
}

export interface ISessionState {
  status: 'idle' | 'loading' | 'rejected';
  questionListStatus: 'idle' | 'loading' | 'rejected';
  session: ISession;
  questionList: Array<IQuestion>;
  count: number;
  selectedStudent: string;
}

const initialState: ISessionState = {
  status: 'idle',
  questionListStatus: 'idle',
  session: { questions: [], subTopicId: '', student: '' },
  questionList: [],
  count: 0,
  selectedStudent: '',
};

export const SessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    resetSession: (state) => {
      state.session = initialState.session;
    },
    selectStudent: (state, action) => {
      state.selectedStudent = action.payload;
    },
    resetQuestionList: (state) => {
      state.questionList = initialState.questionList;
      state.count = initialState.count;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Session
      .addCase(getSessionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSessionById.fulfilled, (state, action) => {
        state.status = 'idle';
        const sortedQuestions = [...action.payload.questions].sort(
          (a, b) => a.pageNo - b.pageNo
        );
        const newQuestions = sortedQuestions.map((question, index) => ({
          ...question,
          pageNo: question.pageNo + index,
        }));
        state.session = { ...action.payload, questions: newQuestions };
      })
      .addCase(getSessionById.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get Question List
      .addCase(getQuestionListBySubTopic.pending, (state) => {
        state.questionListStatus = 'loading';
      })
      .addCase(getQuestionListBySubTopic.fulfilled, (state, action) => {
        state.questionListStatus = 'idle';
        state.questionList = action.payload.docs;
        state.count = action.payload.totalDocs;
      })
      .addCase(getQuestionListBySubTopic.rejected, (state) => {
        state.questionListStatus = 'rejected';
      })

      // Load More Question List
      .addCase(loadMoreQuestionListBySubTopic.pending, (state) => {
        state.questionListStatus = 'loading';
      })
      .addCase(loadMoreQuestionListBySubTopic.fulfilled, (state, action) => {
        state.questionListStatus = 'idle';
        const newList = [...state.questionList, ...action.payload.docs];
        state.questionList = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(loadMoreQuestionListBySubTopic.rejected, (state) => {
        state.questionListStatus = 'rejected';
      });
  },
});

export const {
  resetSession,
  selectStudent,
  resetQuestionList,
} = SessionSlice.actions;

export const getSessionState = (state: RootState): RootState['session'] =>
  state.session;

export * from './SessionApi';
export default SessionSlice.reducer;
